import { Button, Grid, IconButton, makeStyles, Switch, Theme, Typography } from "@material-ui/core";
import classNames from "classnames";
import React, { useMemo, useState } from "react"
import { LibraryItem } from "../../../entities/LibraryItem";
import { LibraryItemType } from "../../../entities/LibraryItemType";
import { LinkAssetType } from "../../../entities/LinkAssetType";
import { FieldValidationError } from "../../../services/server/ServerResponses";

import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import CloudOffIcon from "@material-ui/icons/CheckCircle";
import CloudOnIcon from "@material-ui/icons/CheckCircleOutline";
import EditIcon from "@material-ui/icons/Edit";
import { useLibraryAlert, useLibrary } from "../LibraryPage";
import { DetailRow } from "./Components/DetailRow";
import DateHelper from "../../../utilities/DateHelper";
import { AssetCategory, printAssetCategory } from "../../../entities/AssetCategory";
import { EditForm, EditFormRequest } from "./Components/EditForm";
import { useOfflineAssets } from "../../offline/useOfflineAssets";
import { BuilderAssetService } from "../../../services/BuilderAssetService";
import { BuilderAsset, convertBuilderAssetToLibraryItem } from "../../../entities/BuilderAsset";
import { DrawerHeader } from "./Components/DrawerHeader";
import { SharingDialog } from "../../../components/SharingDialog/SharingDialog";
import { GridGrow } from "../../../components/GridGrow";
import { LinkAssetService, SaveLinkAssetRequest } from "../../../services/LinkAssetService";
import { convertLinkAssetToLibraryItem } from "../../../entities/LinkAsset";
import { FileAssetService, UpdateFileAssetRequest } from "../../../services/FIleAssetService";
import { convertFileAssetToLibraryItem } from "../../../entities/FileAsset";

const useStyles = makeStyles((theme: Theme) => ({
    infoContainer: {
        fontFamily: "Roboto, Helvetica",
        fontSize: "13px",
        color: "#202124",
    },
    infoRow: {
        fontFamily: "Roboto, Helvetica",
        fontSize: 13,
        color: "#202124", 
        paddingTop: 6, 
        paddingBottom: 6,
    },
    infoProp: {
        color: "#5f6368",
    },
    inlineIcon: {
        color: "rgb(143, 143, 143)",
        fontSize: 16,
        verticalAlign: "text-bottom",
        float: "right",
        marginRight: 6,
        cursor: "pointer",
    },
    infoValue: {
        boxSizing: "border-box",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 500,
    },
    infoClickable: {
        cursor: "pointer",
        color: "#1967d2",
    },
}));


interface Props {
    item: LibraryItem;
}

const buildTypeKind = (type?: LibraryItemType, category?: AssetCategory) => {
    let displayKind: string = "";

    if (type === LibraryItemType.FileAsset) {
        displayKind = "File";
    } else if (type === LibraryItemType.LinkAsset) {
        displayKind = "Link";
    }

    return `${category ? printAssetCategory(category) : ""} ${displayKind}`;
};

export function AssetView(props:Props){
    const { item } = props;
    const classes = useStyles();
    const [editMode, setEditMode] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
    const [formData, setFormData] = useState<EditFormRequest | null>(null);
    const libraryAlert = useLibraryAlert();
    const context = useLibrary();

    const { setAssetOfflineMode } = useOfflineAssets();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useMemo(() => setEditMode(false) , [item])

    const onOfflineEnabledToggle = async() => {
        setAssetOfflineMode(item.id, !item.offlineMode);

        setDisabled(true)
        const response = await BuilderAssetService.setOfflineMode(item.id, !item.offlineMode);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertBuilderAssetToLibraryItem(asset));
            libraryAlert(`${asset.name} ${asset.offlineMode ? "is available offline" : "is no longer available offline"}`)
        }
    }

    const handleStar = () => {
        switch(item.type){
            case LibraryItemType.BuilderAsset:
                return handleStarBuilderAsset();
            case LibraryItemType.FileAsset:
                return handleStarFile();
            case LibraryItemType.LinkAsset:
                return handleStarLink();
        }
    }

    const handleStarBuilderAsset = async() => {
        setDisabled(true)
        const response = await BuilderAssetService.setStarred(item.id, !item.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertBuilderAssetToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
    }

    const handleStarFile = async() => {
        setDisabled(true)
        const response = await FileAssetService.setStarred(item.id, !item.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertFileAssetToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
    }

    const handleStarLink = async() => {
        setDisabled(true)
        const response = await LinkAssetService.setStarred(item.id, !item.isStarred);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            const asset = response.data;
            context.updateLibraryItem(convertLinkAssetToLibraryItem(asset));
            libraryAlert(`${asset.isStarred ? "Star added to" : "Star removed from"} ${asset.name}`)
        }
    }

    const handleAssetDetailsSave = async () => {
		if (!formData) {
			return;
		}

		if (item.type === LibraryItemType.LinkAsset) {
			await saveLinkAsset(item, formData);
		} else if (item.type === LibraryItemType.BuilderAsset) {
			await saveBuilderAsset(item, formData);
		} else if (item.type === LibraryItemType.FileAsset) {
			await saveFileAsset(item, formData);
		}
	};

	const saveLinkAsset = async (item: LibraryItem, inputs:EditFormRequest) => {
		const updatedAsset: SaveLinkAssetRequest = {
			id: item.id,
			parentId: item.parentId,
			name: inputs.title,
			isStarred: item.isStarred,
			dateAccessed: item.dateAccessed,
			category: item.category!,
			url: inputs.url ?? item.linkUrl!,
			locationPreached: inputs.locationPreached ?? "",
			scripture: inputs.scriptureReferences ?? "",
			datePreached: inputs.preachDate ?? undefined
		};

        setDisabled(true)
		const response = await LinkAssetService.save(updatedAsset);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.validation) return setErrors(response.errors);

        context.updateLibraryItem(convertLinkAssetToLibraryItem(response.data))
	};

	const saveBuilderAsset = async (item: LibraryItem, inputs:EditFormRequest) => {
        setDisabled(true)
        const builderAssetResponse = await BuilderAssetService.get(item.id);

        if(builderAssetResponse.error) return libraryAlert(builderAssetResponse.message)
        const asset = builderAssetResponse.data;

		const updatedAsset: BuilderAsset = {
			...asset,
			locationPreached: inputs.locationPreached ?? "",
			scripture: inputs.scriptureReferences ?? "",
			datePreached: inputs.preachDate ?? undefined,
			isStarred: item.isStarred,
			name: inputs.title,
			templateDescription: inputs.templateDescription ?? "",
		};

		const response = await BuilderAssetService.save(updatedAsset);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.validation) return setErrors(response.errors);

        context.updateLibraryItem(convertBuilderAssetToLibraryItem(response.data))
	};

	const saveFileAsset = async (item: LibraryItem, inputs:EditFormRequest) => {
		const updatedAsset: UpdateFileAssetRequest = {
			id: item.id,
			parentId: item.parentId,
			name: inputs.title,
			isStarred: item.isStarred,
			locationPreached: inputs.locationPreached ?? "",
			scripture: inputs.scriptureReferences ?? "",
			datePreached: inputs.preachDate ?? undefined
		};

        setDisabled(true)
		let response = await FileAssetService.updateFileAsset(updatedAsset);
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.validation) return setErrors(response.errors);

        context.updateLibraryItem(convertFileAssetToLibraryItem(response.data))
	};

    return (
        <>
            <DrawerHeader item={item} />
            <div>
                {item.type === LibraryItemType.BuilderAsset
                    &&
                    <Grid container alignItems="center">
                        <Grid item xs={6}>
                            <Typography variant="subtitle1">
                                { item.offlineMode ? "Offline Enabled" : "Offline Disabled"}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Switch 
                                color="primary" 
                                onChange={() => onOfflineEnabledToggle()} 
                                checked={item.offlineMode} 
                                />
                        </Grid>
                    </Grid>
                }
                <Grid container alignItems="center">
                    <Grid item>
                        <Typography variant="subtitle1">
                            {editMode ? "Edit Details" : "Details"}
                            {item.isTemplate && (
                                <>
                                    &nbsp;- <strong>Custom Template</strong>
                                </>
                            )}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <IconButton onClick={() => setEditMode(!editMode)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Grid>
                </Grid>
                <div className={classes.infoContainer}>
                    {editMode
                    ? <EditForm item={item} errors={errors} onFormUpdate={(formData) => setFormData(formData)} />
                    : <>
                            <Grid container alignItems="center" className={classes.infoRow}>
                                <Grid item xs={5} container alignItems="center" className={classes.infoProp}>
                                    <Grid item>
                                        Title
                                    </Grid>
                                    <GridGrow />
                                    <Grid item>
                                        { item.type === LibraryItemType.BuilderAsset && (
                                            item.offlineMode													
                                            ?
                                            <CloudOffIcon 
                                                onClick={() => onOfflineEnabledToggle()}
                                                className={classes.inlineIcon} 
                                            />
                                            :
                                            <CloudOnIcon 
                                                onClick={() => onOfflineEnabledToggle()}
                                                className={classes.inlineIcon} 
                                            />
                                        )}
                                        {item.isStarred 
                                        ? <StarIcon className={classes.inlineIcon} onClick={() => handleStar()} />
                                        : <StarBorderIcon className={classes.inlineIcon} onClick={() => handleStar()}/>
                                        }
                                    </Grid>
                                </Grid>
                                <Grid item xs={7}
                                    className={classNames(classes.infoValue, classes.infoClickable)}
                                    onClick={() => setEditMode(true)}
                                >
                                    {item.name}
                                </Grid>
                            </Grid>
                            {item.isTemplate 
                            ?
                                <DetailRow property="Template Description" value={item.templateDescription} onClick={() => setEditMode(true)}/>
                            :
                                <>
                                    <DetailRow 
                                        property="Preach Date" 
                                        value={item.datePreached ? DateHelper.buildDateFormat(item.datePreached) : "Not Set"}
                                        onClick={() => setEditMode(true)}
                                    />
                                    <DetailRow 
                                        property="Location" 
                                        value={item.locationPreached ?? ""}
                                        onClick={() => setEditMode(true)}
                                    />
                                    { item.type === LibraryItemType.BuilderAsset && <></>

                                    }
                                    <DetailRow 
                                        property="Scriptures" 
                                        value={item.scripture ?? ""}
                                        onClick={() => setEditMode(true)}
                                    />
                                    {item.type === LibraryItemType.LinkAsset && item.linkType === LinkAssetType.General && (
                                        <DetailRow 
                                            property="Url" 
                                            value={item.linkUrl ?? ""}
                                            onClick={() => setEditMode(true)}
                                        />
                                    )}
                                </>
                            }
                        </>
                    }
                    <DetailRow 
                        property="Type" 
                        value={buildTypeKind(item.type, item.category)}
                    />
                    <DetailRow 
                        property="Modified" 
                        value={DateHelper.buildDateFormat(item.dateModified)}
                    />
                    <DetailRow 
                        property="Access" 
                        value={DateHelper.buildDateFormat(item.dateAccessed)}
                    />
                    <DetailRow 
                        property="Created" 
                        value={DateHelper.buildDateFormat(item.dateCreated)}
                    />
                    {editMode && (
                        <div style={{textAlign: "right", paddingRight: 16,}}>
                            <Button onClick={() => setEditMode(false)} color="primary" disabled={disabled}>
                                Cancel
                            </Button>
                            <Button color="primary" disabled={disabled} onClick={() => handleAssetDetailsSave()}>
                                Save
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}